import React from "react"
import autobind from "class-autobind"
import strings from "../../localization/strings"
import AccountStore from "../../stores/AccountStore"
import AppStateStore from "../../stores/AppStateStore"
import { IDate, IDateRange, IUIUser, IJobDocument, IJobPhase, IEquipmentDocument, IPunchBundle } from "../../models"
import { Table, TableHeader, TableRow, TableHeaderColumn, TableBody, TableRowColumn, FloatingActionButton, IconButton } from "material-ui"
import ContentAdd from "material-ui/svg-icons/content/add"
import PunchAuditToolTipColumn from "./PunchAuditToolTipColumn"
import PunchImageToolTipColumn from "./PunchImageToolTipColumn"
import PunchMapToolTipColumn from "./PunchMapToolTipColumn"
import IconDatePicker from "../common/IconDatePicker"
import SimpleTimeEditor from "../common/SimpleTimeEditor"
import * as timeHelper from "../../helpers/timeHelper"
import * as punchHelper from "../../helpers/punchHelper"
import moment from "moment"
import { grey500, grey100 } from "material-ui/styles/colors"
import DeleteIcon from "material-ui/svg-icons/action/delete"
import ChooseHierarchyJobPhaseChipForPunch from "../hierarchy/choose_hierarchy/ChooseHierarchyJobPhaseChipForPunch"
import EquipmentChip from "../equipment/EquipmentChip"
import AddPunchDialog from "./AddPunchDialog"
import Position_PayType_PayClass_Selector from "../position_paytype_payclass_selector/Position_PayType_PayClass_Selector"
import _ from "lodash"
import { checkPositionFieldAvailable } from "../../helpers/positionHelper"

interface IExtendPunchBundle extends IPunchBundle {
    hierarchyLevelIntentionalChanged: boolean
}
interface IProps {
    referenceDate: IDate
    user: IUIUser
    punchBundles: IPunchBundle[]
    lockedPeriods: IDateRange[]
    payrollHierarchyId: string
	payrollPayGroupId: string
}
interface IState {
    openAddPunchDialog: boolean
    punchBundles: IExtendPunchBundle[]
}

export default class EditPunchTable extends React.Component<IProps, IState> {
    private punchLevelColWidth = 0
    private hierarchyColWidth = 150
    private jobphaseColWidth = 125
    private tableColsMarginWidth = 5
    private selectColWidth = 100
    private positionPayClassPayTypeColMarginWidth = 10
    private positionPayClassPayTypeColWidth = (this.selectColWidth + this.positionPayClassPayTypeColMarginWidth * 2) * 2
    private originalPunchIds: string[] = []
    private addPunchIds: string[] = []
    private updatePunchIds: string[] = []
    private deletePunchIds: string[] = []
    private errorPunchIds: string[] = []
    private errorPositionPunchIds: string[] = []
    constructor(props: IProps) {
        super(props)
        this.state = {
            punchBundles: (props.punchBundles as IExtendPunchBundle[]).map(i => {
                i.hierarchyLevelIntentionalChanged = false
                return i
            }),
            openAddPunchDialog: false
        }
        autobind(this)
        this.originalPunchIds = props.punchBundles.map(i => i.Punch.Id)
        this.punchLevelColWidth = this.hierarchyColWidth + 2 * this.tableColsMarginWidth
        if (AccountStore.getJobsEnabled())
            this.punchLevelColWidth += this.jobphaseColWidth + 2 * this.tableColsMarginWidth
        if (AccountStore.getPhasesEnabled())
            this.punchLevelColWidth += this.jobphaseColWidth + 2 * this.tableColsMarginWidth
        if (AccountStore.getPayClassEnabled())
            this.positionPayClassPayTypeColWidth += this.selectColWidth + this.positionPayClassPayTypeColMarginWidth * 2
    }

    handleErrorPunchIds(punchId: string, error: boolean) {
        if (error) {
            !this.errorPunchIds.includes(punchId) &&
                this.errorPunchIds.push(punchId)
        }
        else {
            this.errorPunchIds = this.errorPunchIds.filter(i => i !== punchId)
        }
    }

    handleErrorPositionPunchIds(punchId: string, error: boolean) {
        if (error) {
            !this.errorPositionPunchIds.includes(punchId) &&
                this.errorPositionPunchIds.push(punchId)
        }
        else {
            this.errorPositionPunchIds = this.errorPositionPunchIds.filter(i => i !== punchId)
        }
    }

    async handleUpdate(punchBundle: IExtendPunchBundle, updateFunc: () => Promise<IExtendPunchBundle>, updated: boolean = true) {
        updated && this.originalPunchIds.includes(punchBundle.Punch.Id) && !this.updatePunchIds.includes(punchBundle.Punch.Id) &&
            this.updatePunchIds.push(punchBundle.Punch.Id)
        const indexOf = this.state.punchBundles.findIndex(i => i.Punch.Id === punchBundle.Punch.Id)
        if (indexOf > -1) {
            punchBundle = await updateFunc()
            this.state.punchBundles[indexOf] = punchBundle
            this.setState({ punchBundles: this.state.punchBundles })
        }
    }

    async handleUpdateDateTimeLocal(punchBundle: IExtendPunchBundle, date: IDate, hour: number, minute: number) {
        this.handleUpdate(punchBundle, async () => {
            const dateTimeLocal = moment(date).startOf("day").hour(hour).minute(minute).utc(true)
            punchBundle.Punch.DateTimeLocal = dateTimeLocal.format()
            const timezoneId = await timeHelper.getIANATimezoneNameFromHierarchyJobId(punchBundle.Punch.HierarchyItemId, punchBundle.Punch.JobId)
            punchBundle.Punch.DateTime = timeHelper.getTimezoneTranslatedDateTime(date, dateTimeLocal.minute(), dateTimeLocal.hour(), timezoneId)
            return punchBundle
        })
    }

    handleUpdatePunchLevel(punchBundle: IExtendPunchBundle, hierarchyId: string, job: IJobDocument, phase: IJobPhase, intentionalChanged: boolean) {
        this.handleUpdate(punchBundle, async () => {
            punchBundle.Punch.HierarchyItemId = hierarchyId
            punchBundle.Punch.JobId = job ? job.Id : null
            punchBundle.Punch.JobName = job ? job.Name : null
            punchBundle.Punch.JobCostCode = job ? job.CostCode : null
            punchBundle.Punch.PhaseId = phase ? phase.Id : null
            punchBundle.Punch.PhaseName = phase ? phase.Name : null
            punchBundle.Punch.PhaseCostCode = phase ? phase.CostCode : null
            punchBundle.hierarchyLevelIntentionalChanged = intentionalChanged ? true : punchBundle.hierarchyLevelIntentionalChanged
            return punchBundle
        })
    }

    handleUpdateEquipment(punchBundle: IExtendPunchBundle, equipmentList: IEquipmentDocument[]) {
        this.handleUpdate(punchBundle, async () => {
            const equipment = equipmentList.length > 0 ? equipmentList[0] : null
            punchBundle.Punch.EquipmentId = equipment ? equipment.Id : null
            punchBundle.Punch.EquipmentName = equipment ? equipment.Name : null
            return punchBundle
        })
    }

    render() {
        return (
            <div style={{ marginTop: 10, position: "relative" }}>
                <FloatingActionButton
                    data-intercom-target="AddEditPunches-Btn-AddPunch"
                    mini
                    style={{ zIndex: 999, position: "absolute", float: "right", right: 10, top: -10 }}
                    secondary
                    onClick={() => this.setState({ openAddPunchDialog: true })}
                >
                    <ContentAdd />
                </FloatingActionButton>
                <Table
                    selectable={false}
                    multiSelectable={false}
                    bodyStyle={{ overflow: "initial" }}
                >
                    <TableHeader
                        displaySelectAll={false}
                        adjustForCheckbox={false}
                        enableSelectAll={false}
                    >
                        <TableRow style={{ paddingTop: 0 }}>
                            <TableHeaderColumn style={styles.iconCol} />
                            <TableHeaderColumn style={styles.iconCol} />
                            <TableHeaderColumn style={styles.iconCol} />
                            <TableHeaderColumn />
                            <TableHeaderColumn style={styles.dateCol}>{strings.Date}</TableHeaderColumn>
                            <TableHeaderColumn style={styles.timeCol}>{strings.Time}</TableHeaderColumn>
                            <TableHeaderColumn />
                            <TableHeaderColumn style={{ width: this.punchLevelColWidth, paddingLeft: 0, paddingRight: 0 }}>
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <div style={{ width: this.hierarchyColWidth, marginLeft: this.tableColsMarginWidth, marginRight: this.tableColsMarginWidth }}>{strings.Hierarchy}</div>
                                    {AccountStore.getJobsEnabled() &&
                                        <div style={{ width: this.jobphaseColWidth, marginLeft: this.tableColsMarginWidth, marginRight: this.tableColsMarginWidth }}>{strings.Job}</div>
                                    }
                                    {AccountStore.getPhasesEnabled() &&
                                        <div style={{ width: this.jobphaseColWidth, marginLeft: this.tableColsMarginWidth, marginRight: this.tableColsMarginWidth }}>{ punchHelper.checkPhaseFieldAvailable("singular") ? AccountStore.getCurrentAccountSettings().CustomFieldSettings.PhaseFieldLabels.SingularPhaseLabel : strings.Phase}</div>
                                    }
                                </div>
                            </TableHeaderColumn>
                            <TableHeaderColumn />
                            {AccountStore.getEquipmentEnabled() &&
                                <TableHeaderColumn style={styles.equipmentCol}>{strings.Equipment}</TableHeaderColumn>
                            }
                            <TableHeaderColumn />
                            <TableHeaderColumn style={{ width: this.positionPayClassPayTypeColWidth, paddingLeft: 0, paddingRight: 0 }}>
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <div style={{ width: this.selectColWidth, marginLeft: this.positionPayClassPayTypeColMarginWidth, marginRight: this.positionPayClassPayTypeColMarginWidth }}>{checkPositionFieldAvailable('plural') ? AccountStore.getCurrentAccountSettings().CustomFieldSettings.PositionFieldLabels.SingularPositionLabel : strings.Position}</div>
                                    {AccountStore.getPayClassEnabled() &&
                                        <div style={{ width: this.selectColWidth, marginLeft: this.positionPayClassPayTypeColMarginWidth, marginRight: this.positionPayClassPayTypeColMarginWidth }}>{strings.Pay_Class}</div>
                                    }
                                    <div style={{ width: this.selectColWidth, marginLeft: this.positionPayClassPayTypeColMarginWidth, marginRight: this.positionPayClassPayTypeColMarginWidth }}>{strings.Pay_Type}</div>
                                </div>
                            </TableHeaderColumn>
                            <TableHeaderColumn />
                            {AccountStore.getCalculationDayGroupEnabled() &&
                                <TableHeaderColumn style={styles.calcDateCol}>{strings.Calculation_Date}</TableHeaderColumn>
                            }
                            <TableHeaderColumn />
                            <TableHeaderColumn style={styles.deleteCol} />
                        </TableRow>
                    </TableHeader>
                    <TableBody displayRowCheckbox={false}>
                        {(() => {
                            let greyRow = true
                            let rowDate = moment().startOf("day")
                            return this.state.punchBundles
                                .sort((a, b) => {
                                    const aTime = moment(a.Punch.DateTimeLocal)
                                    const bTime = b.Punch.DateTimeLocal
                                    return aTime.isSame(bTime) ? 0 : aTime.isAfter(bTime) ? 1 : -1
                                })
                                .map((punchBundle, index) => {
                                    const punch = punchBundle.Punch
                                    const dateTimeLocal = timeHelper.keepConvertUTCToLocal(punch.DateTimeLocal)
                                    const hour = moment(dateTimeLocal).hour()
                                    const minute = moment(dateTimeLocal).minute()
                                    const dayGroupingDay = timeHelper.keepConvertUTCToLocal(punch.DayGroupingTimeLocal)
                                    const readonly = punchHelper.getPunchLocked(punchBundle.PunchSetDayGroupingTimeLocal, this.props.lockedPeriods)
                                    if (index === 0) {
                                        rowDate = moment(dateTimeLocal).startOf("day")
                                    }
                                    else {
                                        if (!rowDate.isSame(moment(dateTimeLocal).startOf("day"))) {
                                            greyRow = !greyRow
                                            rowDate = moment(dateTimeLocal).startOf("day")
                                        }
                                    }
                                    return (
                                        <TableRow key={punch.Id} style={{ backgroundColor: (readonly || greyRow) && grey100 }}>
                                            <TableRowColumn style={styles.iconCol}>
                                                <PunchAuditToolTipColumn punch={punch} />
                                            </TableRowColumn>
                                            <TableRowColumn style={styles.iconCol}>
                                                <PunchImageToolTipColumn punch={punch} iconStyle={{ fontSize: 16 }} />
                                            </TableRowColumn>
                                            <TableRowColumn style={styles.iconCol}>
                                                <PunchMapToolTipColumn punch={punch} iconStyle={{ fontSize: 16 }} />
                                            </TableRowColumn>
                                            <TableRowColumn />
                                            <TableRowColumn style={styles.dateCol}>
                                                <IconDatePicker
                                                    label={""}
                                                    width={95}
                                                    readonly={readonly}
                                                    date={dateTimeLocal}
                                                    textFieldStyle={{ fontSize: 12 }}
                                                    datePickerStyle={{ marginLeft: 19 }}
                                                    iconStyle={{ left: 0, fontSize: 15, top: 15 }}
                                                    formatDate={date => moment(date).format("ddd MM/DD/YYYY")}
                                                    handleSelectDate={date => this.handleUpdateDateTimeLocal(punchBundle, date, hour, minute)}
                                                />
                                            </TableRowColumn>
                                            <TableRowColumn style={styles.timeCol}>
                                                <div style={{ display: "flex", alignItems: "center", textAlign: "center" }}>
                                                    <i className="fa fa-clock-o" style={{ marginRight: 5, color: grey500, fontSize: 15 }} />
                                                    <SimpleTimeEditor
                                                        readonly={readonly}
                                                        textFieldStyle={{ fontSize: 12 }}
                                                        simpleTime={{ Hour: hour, Minute: minute }}
                                                        handleSlowChange={simpleTime => this.handleUpdateDateTimeLocal(punchBundle, dateTimeLocal, simpleTime.Hour, simpleTime.Minute)}
                                                    />
                                                </div>
                                            </TableRowColumn>
                                            <TableRowColumn />
                                            <TableRowColumn style={{ width: this.punchLevelColWidth, paddingLeft: 0, paddingRight: 0 }}>
                                                <ChooseHierarchyJobPhaseChipForPunch
                                                    hideIcon
                                                    readonly={readonly}
                                                    hierarchyChipLabel={null}
                                                    jobChipLabel={null}
                                                    phaseChipLabel={null}
                                                    employee={this.props.user}
                                                    punchHierarchyItemId={punch.HierarchyItemId}
                                                    jobId={punch.JobId}
                                                    phaseId={punch.PhaseId}
                                                    handleSelectHierarchyJobPhase={(hierarchyLevelId, errorHierarchyId, job, phase, intentionalChanged) => {
                                                        this.handleErrorPunchIds(punch.Id, errorHierarchyId ? true : false)
                                                        this.handleUpdatePunchLevel(punchBundle, hierarchyLevelId, job, phase, intentionalChanged)
                                                    }}
                                                    isEmployeePermissionPriority
                                                    isPunchListTable
                                                    hierarchyColWidth={this.hierarchyColWidth}
                                                    jobColWidth={this.jobphaseColWidth}
                                                    phaseColWidth={this.jobphaseColWidth}
                                                    tableColsMarginWidth={this.tableColsMarginWidth}
                                                    searchJobByEmpPermission
                                                />
                                            </TableRowColumn>
                                            <TableRowColumn />
                                            {AccountStore.getEquipmentEnabled() &&
                                                <TableRowColumn style={styles.equipmentCol}>
                                                    <EquipmentChip
                                                        canChange={!readonly}
                                                        defaultEquipmentId={punch.EquipmentId}
                                                        setEquipmentList={equipmentList => this.handleUpdateEquipment(punchBundle, equipmentList)}
                                                        isPunchListTable
                                                        equipmentColWidth={100}
                                                    />
                                                </TableRowColumn>
                                            }
                                            <TableRowColumn />
                                            <TableRowColumn style={{ width: this.positionPayClassPayTypeColWidth, paddingLeft: 0, paddingRight: 0 }}>
                                                <Position_PayType_PayClass_Selector
                                                    readonly={readonly}
                                                    date={dateTimeLocal}
                                                    employee={this.props.user}
                                                    hierarchyId={punch.HierarchyItemId}
                                                    shiftId={punch.ShiftId}
                                                    jobId={punch.JobId}
                                                    phaseId={punch.PhaseId}
                                                    selectedPositionId={punch.PositionId}
                                                    selectedPayTypeId={punch.PayTypeId}
                                                    selectedPayClassId={punch.PayClassId}
                                                    keepPropsPayClassId_PayTypeId
                                                    handleLoadingPositions={loading => { }}
                                                    handleSelectPosition={(position, errorPosition) => {
                                                        this.handleErrorPositionPunchIds(punch.Id, errorPosition ? true : false)
                                                        const positionId = position ? position.Id : null
                                                        this.handleUpdate(
                                                            punchBundle,
                                                            async () => {
                                                                punchBundle.PunchPosition = AccountStore.getPayClassEnabled() && position ? { Id: punchBundle.Punch.Id, Position: position } : null
                                                                punchBundle.Punch.PositionId = positionId
                                                                return punchBundle
                                                            },
                                                            punchBundle.Punch.PositionId !== positionId
                                                        )
                                                    }}
                                                    handleSelectPayClass={payClassId =>
                                                        this.handleUpdate(
                                                            punchBundle,
                                                            async () => {
                                                                punchBundle.Punch.PayClassId = payClassId
                                                                return punchBundle
                                                            },
                                                            punchBundle.Punch.PayClassId !== payClassId
                                                        )
                                                    }
                                                    handleSelectPayType={(payTypeId, errorPayTypeId) => {
                                                        this.handleErrorPunchIds(punch.Id, errorPayTypeId ? true : false)
                                                        this.handleUpdate(
                                                            punchBundle,
                                                            async () => {
                                                                punchBundle.Punch.PayTypeId = payTypeId
                                                                return punchBundle
                                                            },
                                                            punchBundle.Punch.PayTypeId !== payTypeId
                                                        )
                                                    }}
                                                    referencePunchPositionDocumentId={punch.Id}

                                                    isPunchListTable
                                                    tableColWidth={this.selectColWidth}
                                                    tableColsMarginWidth={this.positionPayClassPayTypeColMarginWidth}
                                                    hierarchyLevelIntentionalChanged={punchBundle.hierarchyLevelIntentionalChanged}
                                                    hideIcons
                                                />
                                            </TableRowColumn>
                                            <TableRowColumn />
                                            {AccountStore.getCalculationDayGroupEnabled() &&
                                                <TableRowColumn style={styles.calcDateCol}>
                                                    <IconDatePicker
                                                        label={""}
                                                        width={95}
                                                        readonly
                                                        date={dayGroupingDay}
                                                        textFieldStyle={{ fontSize: 12 }}
                                                        datePickerStyle={{ marginLeft: 19 }}
                                                        iconStyle={{ left: 0, fontSize: 15, top: 15 }}
                                                        handleSelectDate={date =>
                                                            this.handleUpdate(punchBundle, async () => {
                                                                punchBundle.Punch.DayGroupingTimeLocal = moment(date).startOf("day").hour(moment(dayGroupingDay).hour()).minute(moment(dayGroupingDay).minute()).utc(true).format()
                                                                return punchBundle
                                                            })
                                                        }
                                                        formatDate={date => moment(date).format("MM/DD/YYYY")}
                                                    />
                                                </TableRowColumn>
                                            }
                                            <TableRowColumn />
                                            <TableRowColumn style={styles.deleteCol}>
                                                {!readonly &&
                                                    <IconButton
                                                        style={{ cursor: "pointer", paddingLeft: 0, paddingRight: 0, width: 25 }}
                                                        onClick={() => {
                                                            AppStateStore.setConfirmDialogContent({
                                                                content: strings.Are_you_sure_delete_Punch,
                                                                confirm: () => {
                                                                    this.originalPunchIds.includes(punch.Id) &&
                                                                        this.deletePunchIds.push(punch.Id)
                                                                    this.handleErrorPunchIds(punch.Id, false)
                                                                    this.addPunchIds = this.addPunchIds.filter(i => i !== punch.Id)
                                                                    this.updatePunchIds = this.updatePunchIds.filter(i => i !== punch.Id)
                                                                    this.setState({ punchBundles: this.state.punchBundles.filter(i => i.Punch.Id !== punch.Id) })
                                                                }
                                                            })
                                                        }}
                                                    >
                                                        <DeleteIcon color={grey500} />
                                                    </IconButton>
                                                }
                                            </TableRowColumn>
                                        </TableRow>
                                    )
                                })
                        })()}
                    </TableBody>
                </Table>
                {this.state.openAddPunchDialog &&
                    <AddPunchDialog
                        date={moment(this.props.referenceDate).utc()}
                        user={this.props.user}
                        onRequestClose={() => this.setState({ openAddPunchDialog: false })}
                        addPunch={async (userId: string, punchBundle: IPunchBundle) => {
                            const dateTimeClientLocal = await timeHelper.getDateTime(punchBundle.Punch.HierarchyItemId, punchBundle.Punch.JobId, punchBundle.Punch.DateTime)
                            const dateTimeLocal = dateTimeClientLocal.utc(true).format()
                            const extendPunchBundle = punchBundle as IExtendPunchBundle
                            extendPunchBundle.Punch.DateTimeLocal = dateTimeLocal
                            extendPunchBundle.Punch.DayGroupingTime = punchBundle.Punch.DateTime
                            extendPunchBundle.Punch.DayGroupingTimeLocal = dateTimeLocal
                            extendPunchBundle.hierarchyLevelIntentionalChanged = false
                            this.addPunchIds.push(extendPunchBundle.Punch.Id)
                            this.state.punchBundles.push(extendPunchBundle)
                            this.setState({
                                openAddPunchDialog: false,
                                punchBundles: this.state.punchBundles
                            })
                        }}
                        hierarchyId={this.props.user.DefaultPunchHierarchyId}
                        positionId={this.props.user.DefaultPositionId}
                        payClassId={null}
                        payTypeId={null}
                        shiftId={null}
                        breakId={null}
                        mobileBreakId={null}
                        jobId={this.props.user.DefaultJobId}
                        phaseId={this.props.user.DefaultPhaseId}
                        equipmentId={null}
                        isPunchIn
                        pairedPunchId={null}
                        isEmployeePermissionPriority
                        useEditablePunchTableDataStore
                        payrollHierarchyId={this.props.payrollHierarchyId}
                        payrollPayGroupId={this.props.payrollPayGroupId}
                    />
                }
            </div>
        )
    }
}

const styles = {
    iconCol: {
        paddingLeft: 0,
        paddingRight: 0,
        width: 25
    },
    dateCol: {
        paddingLeft: 5,
        paddingRight: 5,
        width: 115
    },
    timeCol: {
        paddingLeft: 5,
        paddingRight: 5,
        width: 100
    },
    equipmentCol: {
        paddingLeft: 5,
        paddingRight: 5,
        width: 100
    },
    calcDateCol: {
        paddingLeft: 5,
        paddingRight: 5,
        width: 100
    },
    deleteCol: {
        paddingLeft: 5,
        paddingRight: 5,
        width: 25
    }
}